import { matchSorter } from 'match-sorter';

// Utils
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { sortSocialProfiles } from '@/utils/sort/profiles/sortSocialProfiles';
import { useProfilesStore } from '@/utils/zustand/providers/profiles-provider';

// Types
import type { SocialProfileType } from '@/types/socialProfileType';

// Components
import { SearchBox } from '@/components/composite/SearchBox/SearchBox';
import { SaveAsGroupButton } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/ProfilesTab/SaveAsGroupButton';
import { SearchItems } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/ProfilesTab/SearchItems';
import { SearchInput } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/SearchInput';
import { SearchListWrapper } from '@/components/layouts/LayoutPlatform/components/Header/components/ManageProfiles/Popover/SearchListWrapper';

export type SocialProfileFormattedType = SocialProfileType & {
    selected: boolean;
};

export type ProfilesTabsProps = {
    handleSaveSelectedProfiles: (args: SocialProfileType['id'][]) => void;
};

export function ProfilesTab({ handleSaveSelectedProfiles }: ProfilesTabsProps) {
    const { t } = useTranslation('layout_platform');
    const { manageProfiles, activeSocialProfileId, activeMultiSelectionProfiles, activeGroupId } =
        useProfilesStore((state) => state);
    const { activatedSocialProfiles } = useSocialProfiles({ revalidateIfStale: false });

    const getInitialData = () => {
        const formattedProfiles = activatedSocialProfiles.map((profile) => {
            const isSelected = Boolean(
                !activeGroupId &&
                    (profile.id === activeSocialProfileId ||
                        activeMultiSelectionProfiles?.socialProfileIds.some(
                            (id) => id === profile.id
                        ))
            );
            return {
                ...profile,
                selected: isSelected
            };
        });

        return matchSorter(formattedProfiles, '', {
            baseSort: (a, b) => sortSocialProfiles('sort_asc', a.item, b.item)
        });
    };

    return (
        <SearchBox
            searchKeys={{
                threshold: matchSorter.rankings.CONTAINS,
                keys: ['slug', 'external_api_data.name']
            }}
            inputId="profiles"
            initialData={getInitialData()}>
            <SearchBox.Wrapper<SocialProfileFormattedType>
                withoutStyle
                data-testid="search-box-wrapper-profiles"
                className="flex h-80 flex-col"
                onSelectionChange={manageProfiles}>
                <SearchInput placeholder={t('manage_profiles.tab_profiles.search_placeholder')} />
                <SearchListWrapper
                    type="profiles"
                    no_data_message={t(
                        'layout_platform:manage_profiles.tab_profiles.search_no_result'
                    )}>
                    <SearchItems displayErrorWarning />
                    <SaveAsGroupButton handleSaveSelectedProfiles={handleSaveSelectedProfiles} />
                </SearchListWrapper>
            </SearchBox.Wrapper>
        </SearchBox>
    );
}
